
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useStableAvatar } from "@/hooks/use-stable-avatar";
import { useToast } from "@/hooks/use-toast";

export function useProfileCredits() {
  const { currentProfile } = useStableAvatar();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: creditBalance = 0, isLoading } = useQuery({
    queryKey: ['profileCreditBalance', currentProfile?.id],
    queryFn: async () => {
      if (!currentProfile?.id) {
        console.log('No current profile for credit balance');
        return 0;
      }
      
      console.log('Fetching credit balance for profile:', currentProfile.id, 'name:', currentProfile.name);
      
      const { data, error } = await supabase.rpc('get_profile_credit_balance', {
        target_profile_id: currentProfile.id
      });
      
      if (error) {
        console.error('Error fetching profile credit balance:', error);
        return 0;
      }
      
      console.log('Credit balance for profile', currentProfile.name, ':', data);
      return data || 0;
    },
    enabled: !!currentProfile?.id,
    staleTime: 30000
  });

  const { data: canSpendCredits } = useQuery({
    queryKey: ['canSpendCredits', currentProfile?.id, 4], // 4 credits for individual book
    queryFn: async () => {
      if (!currentProfile?.id) return false;
      
      const { data, error } = await supabase.rpc('can_profile_spend_credits', {
        target_profile_id: currentProfile.id,
        amount: 4
      });
      
      if (error) {
        console.error('Error checking credit spending ability:', error);
        return false;
      }
      
      return data || false;
    },
    enabled: !!currentProfile?.id,
    staleTime: 30000
  });

  const spendCreditsMutation = useMutation({
    mutationFn: async ({ amount, reason, referenceId }: { 
      amount: number; 
      reason: string; 
      referenceId: string;
    }) => {
      if (!currentProfile?.id) throw new Error('No current profile');
      
      console.log('Spending credits:', amount, 'for profile:', currentProfile.name);
      
      const { data, error } = await supabase.rpc('spend_profile_credits', {
        target_profile_id: currentProfile.id,
        amount,
        reason,
        reference_id: referenceId
      });
      
      if (error) throw error;
      if (!data) throw new Error('Failed to spend credits');
      
      console.log('Successfully spent credits:', amount);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profileCreditBalance'] });
      queryClient.invalidateQueries({ queryKey: ['canSpendCredits'] });
      toast({
        title: "Credits Used",
        description: "Your purchase was successful!",
      });
    },
    onError: (error) => {
      console.error('Credit spending error:', error);
      toast({
        title: "Credit Error",
        description: "Unable to process credit purchase. Please try again.",
        variant: "destructive"
      });
    }
  });

  return {
    creditBalance,
    canSpendCredits,
    isLoading,
    spendCredits: spendCreditsMutation.mutate,
    isSpendingCredits: spendCreditsMutation.isPending
  };
}
